(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global.Transformer = factory());
}(this, (function () { 'use strict';

/**
 * Loops through one or more elements.
 *
 * @param {Element|NodeList} els An element or a NodeList.
 * @param {function} cb Function to call with the element.
 */
function each(els, cb) {
  if (els instanceof Element) {
    cb(els);
  } else {
    [].slice.call(els).forEach(cb);
  }
}

/**
 * Returns true if transform attribute should be used instead of CSS.
 *
 * @param {Element} el Element to test.
 * @returns {boolean} True if transform attribute should be used.
 */
function useTransformAttr(el) {
  return el instanceof SVGElement && el.tagName.toUpperCase() !== 'SVG';
}

// Used by setData and getData.
var dataStore = [];

/**
 * Store data against a given element. IE11 doesn't support dataset on SVG
 * elements, so this is required. It's probably also more performant.
 *
 * @param {Element} el Element to store data against.
 * @param {string} key Key to store data against.
 * @param {*} value Value to store.
 */
function setData(el, key, value) {
  var elStore = dataStore.find(function (store) {
    return store.el === el;
  });

  if (!elStore) {
    dataStore.push({
      el: el,
      data: {}
    });

    elStore = dataStore[dataStore.length - 1];
  }

  elStore.data[key] = value;
}

/**
 * Get data previously stored against a given element.
 *
 * @param {Element} el Element to store data against.
 * @param {string} key Key to store data against.
 * @returns {*} The stored data.
 */
function getData(el, key) {
  var elStore = dataStore.find(function (store) {
    return store.el === el;
  });

  if (!elStore) {
    return undefined;
  }

  return elStore.data[key];
}

// Used internally by the smart arguments logic
var UNCHANGED = Symbol('unchanged');

// An object containing common useful roundings
var dps = {
	'transforms:rotate': 1,
	'transforms:scale': 3,
	'transforms:translate': 1,
	'styles:opacity': 2
};

/**
 * A utility function to call the transform function of a transformer. Handles
 * stuff like smart arguments, and calculating whether to call the function in
 * the first place.
 *
 * This is a utility function, but it's long enough for its own file.
 */
function callFn(type, name, fn, transform, unit, args) {
	var isFirstCall = !fn.args;

	if (isFirstCall) {
		// Smart arguments: calculate the arguments to be passed to the transform
		// function. Either an array (`['x', 'y', ()=>{}}`) or a fn (`(x, y)=>{}`).
		if (typeof fn === 'function') {
			fn.args = fn.toString().match(/\((.*?)\)/)[1].split(',').map(function (str) {
				return str.trim();
			});
		} else {
			fn.args = fn.slice(0, -1);
		}
	}

	var changed = isFirstCall;

	if (fn.args.includes('i')) {
		changed = true;
	}

	if (fn.args.includes('actions') && (Object.keys(args.actions).length || args.actionEnded)) {
		changed = true;
	}

	if (!changed) {
		changed = Object.keys(args.last).some(function (arg) {
			return fn.args.includes(arg) && args[arg] !== args.last[arg];
		});
	}

	// If the arguments haven't changed, don't call the function because the
	// value won't have changed. This assumes that functions are pure: request
	// the `i` argument if it isn't.
	if (!changed) {
		return UNCHANGED;
	}

	var argsForFn = fn.args.map(function (arg) {
		return args[arg];
	});
	var callableFn = typeof fn === 'function' ? fn : fn[fn.length - 1];
	var val = callableFn.apply(transform, argsForFn);

	// Round returned value for extra performance
	if (typeof val === 'number') {
		var roundTo = dps[type + ':' + (Array.isArray(name) ? name[0] : name)];
		if (typeof roundTo !== 'number') {
			roundTo = 3;
		}

		val = Math.round(val * Math.pow(10, roundTo)) / Math.pow(10, roundTo);
	}

	return val + unit;
}

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

// This is where active transformers are stored
var transformers = [];

/**
 * This is the Transformer constructor, called by the user when creating a new
 * transformer object. It is given an array of objects containing transforms.
 *
 * See the README.md file for more.
 *
 * @param {Array} transforms Array of transforms.
 * @constructor
 */
function Transformer$1(transforms) {
	this.i = 0;
	this.transforms = Array.isArray(transforms) ? transforms : [transforms];
	this.visible = undefined;

	this._last = {};
	this._actions = {};
	this._customVariables = {};

	this.start();
}

// Selector to get scroll position from. On the prototype so we can set globally
Transformer$1.prototype.scrollElement = 'window';
Transformer$1.prototype.iIncrease = {
	belowOptimal: 'count',
	aboveOptimal: 'time',
	optimalFps: 60
};

/**
 * Stop the transformer from running
 */
Transformer$1.prototype.stop = function stopTransforms() {
	this.active = false;
	if (transformers.includes(this)) {
		transformers.splice(transformers.indexOf(this), 1);
	}
};

/**
 * Start the transformer again if it was stopped.
 */
Transformer$1.prototype.start = function startTransforms() {
	this.active = true;
	if (!transformers.includes(this)) {
		transformers.push(this);
	}
};

/**
 * Stop the transformer and reset some values like visibility and transforms to
 * their original values.
 */
Transformer$1.prototype.reset = function resetTransforms() {
	this.stop();

	var _iteratorNormalCompletion = true;
	var _didIteratorError = false;
	var _iteratorError = undefined;

	try {
		for (var _iterator = this.transforms[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
			var transform = _step.value;

			if (transform.transforms) {
				each(transform.el, function (el) {
					if (useTransformAttr(el)) {
						el.setAttribute('transform', getData(el, 'originalTransform'));
					} else {
						el.style.transform = getData(el, 'originalTransform');
					}
				});
			}

			if (transform.visible || this.visible) {
				each(transform.el, function (el) {
					el.style.display = getData(el, 'originalDisplay');
				});
			}

			// @todo: should styles be unset?
			// if (transform.styles) {
			// 	for (let [ style, fn, unit = '' ] of transform.styles) {
			// 		each(transform.el, (el) => {
			// 			el.style[style] = '';
			// 		});
			// 	}
			// }

			// @todo: should attrs be unset?
			// if (transform.attrs) {
			// 	for (let [ attr, fn, unit = '' ] of transform.attrs) {
			// 		each(transform.el, (el) => el.removeAttribute(attr));
			// 	}
			// }
		}
	} catch (err) {
		_didIteratorError = true;
		_iteratorError = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion && _iterator.return) {
				_iterator.return();
			}
		} finally {
			if (_didIteratorError) {
				throw _iteratorError;
			}
		}
	}
};

/**
 * Set the y scroll positions the transforms are active between. Similar to the
 * visible property of a transform, but on the entire transformer.
 *
 * @param {[number, number]} visible Min and max y scroll value active at.
 */
Transformer$1.prototype.setVisible = function setGlobalVisible(visible) {
	this.visible = visible;
};

Transformer$1.prototype.trigger = function triggerAction(name, duration) {
	var resolveFn = void 0;
	var promise = void 0;
	if (typeof window.Promise === 'function') {
		promise = new Promise(function (resolve) {
			resolveFn = resolve;
		});
	}

	this._actions[name] = {
		triggered: Date.now(),
		resolveFn: resolveFn,
		duration: duration
	};

	return promise;
};

/**
 * Adds a custom variable that can be used in transformer functions.
 *
 * @param {string} name The name to use for the variables
 * @param {function} getter The function to call to get the variable value.
 */
Transformer$1.prototype.addVariable = function addCustomVar(name, getter) {
	this._customVariables[name] = getter;
};

/**
 * The first function called on requestAnimationFrame. This one calculates the
 * properties to change and what to change them to, but doesn't apply the
 * changes to the DOM. Do not do anything to invalidate the DOM in this
 * function!
 *
 * Separating this process into two parts means that we can call functions that
 * forces styles to be recalculated / a layout in our transform calculation
 * functions without layout thrashing.
 *
 * @private
 */
Transformer$1.prototype._setup = function setupFrame(vars) {
	var _this = this;

	if (!this.active) {
		return;
	}

	var actions = {};

	var _iteratorNormalCompletion2 = true;
	var _didIteratorError2 = false;
	var _iteratorError2 = undefined;

	try {
		for (var _iterator2 = Object.keys(this._actions)[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
			var name = _step2.value;

			var action = this._actions[name];
			var percent = 1 / action.duration * (Date.now() - action.triggered);
			actions[name] = Math.min(percent, 1);
		}
	} catch (err) {
		_didIteratorError2 = true;
		_iteratorError2 = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion2 && _iterator2.return) {
				_iterator2.return();
			}
		} finally {
			if (_didIteratorError2) {
				throw _iteratorError2;
			}
		}
	}

	var _iteratorNormalCompletion3 = true;
	var _didIteratorError3 = false;
	var _iteratorError3 = undefined;

	try {
		var _loop = function _loop() {
			var transform = _step3.value;

			if (_this.i === 0) {
				// This is where data to be put into the DOM is stored until ._frame()
				transform._stagedData = { styles: {}, attrs: {} };

				// This is where data from the last run is kept so it can be compared for changes
				transform._lastData = { styles: {}, attrs: {} };

				// Has to run before visible check
				if (transform.transforms) {
					each(transform.el, function (el) {
						if (useTransformAttr(el)) {
							setData(el, 'originalTransform', (el.getAttribute('transform') || '') + ' ');
						} else {
							var original = el.style.transform;
							setData(el, 'originalTransform', !original || original === 'none' ? '' : original + ' ');
						}
					});
				}
			}

			if (transform.visible || _this.visible) {
				var isHidden = true;
				if (_this.visible) {
					isHidden = vars.y < _this.visible[0] || vars.y > _this.visible[1];
				}

				if (isHidden && transform.visible) {
					isHidden = vars.y < transform.visible[0] || vars.y > transform.visible[1];
				}

				transform._stagedData.isHidden = isHidden;

				if (isHidden) {
					return 'continue';
				}
			} else {
				transform._stagedData.isHidden = undefined;
			}

			var args = Object.assign({
				actions: actions, actionEnded: _this._actionEnded, i: _this.i, last: _this._last
			}, vars);

			if (transform.transforms) {
				var transforms = transform.transforms.map(function (_ref) {
					var _ref2 = slicedToArray(_ref, 3),
					    prop = _ref2[0],
					    fn = _ref2[1],
					    _ref2$ = _ref2[2],
					    unit = _ref2$ === undefined ? '' : _ref2$;

					return [prop, callFn('transforms', prop, fn, transform, unit, args)];
				});

				var changed = transforms.some(function (transform) {
					return transform[1] !== UNCHANGED;
				});

				if (changed && transform._lastData.transforms) {
					changed = transforms.some(function (innerTransform, i) {
						return innerTransform[1] !== transform._lastData.transforms[i][1];
					});
				}

				if (changed) {
					transforms.forEach(function (innerTransform, i) {
						if (innerTransform[1] === UNCHANGED) {
							innerTransform[1] = transform._lastData.transforms[i][1];
						}
					});
				} else {
					transforms = UNCHANGED;
				}

				transform._stagedData.transforms = transforms;

				if (transforms !== UNCHANGED) {
					transform._lastData.transforms = transforms;
				}
			}

			if (transform.styles) {
				var _iteratorNormalCompletion5 = true;
				var _didIteratorError5 = false;
				var _iteratorError5 = undefined;

				try {
					for (var _iterator5 = transform.styles[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
						var _step5$value = slicedToArray(_step5.value, 3),
						    style = _step5$value[0],
						    fn = _step5$value[1],
						    _step5$value$ = _step5$value[2],
						    unit = _step5$value$ === undefined ? '' : _step5$value$;

						var value = callFn('styles', style, fn, transform, unit, args);

						if (value === transform._lastData.styles[style]) {
							value = UNCHANGED;
						}

						transform._stagedData.styles[style] = value;

						if (value !== UNCHANGED) {
							transform._lastData.styles[style] = value;
						}
					}
				} catch (err) {
					_didIteratorError5 = true;
					_iteratorError5 = err;
				} finally {
					try {
						if (!_iteratorNormalCompletion5 && _iterator5.return) {
							_iterator5.return();
						}
					} finally {
						if (_didIteratorError5) {
							throw _iteratorError5;
						}
					}
				}
			}

			if (transform.attrs) {
				var _iteratorNormalCompletion6 = true;
				var _didIteratorError6 = false;
				var _iteratorError6 = undefined;

				try {
					for (var _iterator6 = transform.attrs[Symbol.iterator](), _step6; !(_iteratorNormalCompletion6 = (_step6 = _iterator6.next()).done); _iteratorNormalCompletion6 = true) {
						var _step6$value = slicedToArray(_step6.value, 3),
						    attr = _step6$value[0],
						    fn = _step6$value[1],
						    _step6$value$ = _step6$value[2],
						    unit = _step6$value$ === undefined ? '' : _step6$value$;

						var _value = callFn('attrs', attr, fn, transform, unit, args);

						if (_value === transform._lastData.attrs[attr]) {
							_value = UNCHANGED;
						}

						transform._stagedData.attrs[attr] = _value;

						if (_value !== UNCHANGED) {
							transform._lastData.attrs[attr] = _value;
						}
					}
				} catch (err) {
					_didIteratorError6 = true;
					_iteratorError6 = err;
				} finally {
					try {
						if (!_iteratorNormalCompletion6 && _iterator6.return) {
							_iterator6.return();
						}
					} finally {
						if (_didIteratorError6) {
							throw _iteratorError6;
						}
					}
				}
			}
		};

		for (var _iterator3 = this.transforms[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
			var _ret = _loop();

			if (_ret === 'continue') continue;
		}
	} catch (err) {
		_didIteratorError3 = true;
		_iteratorError3 = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion3 && _iterator3.return) {
				_iterator3.return();
			}
		} finally {
			if (_didIteratorError3) {
				throw _iteratorError3;
			}
		}
	}

	if (this._actionEnded) {
		this._actionEnded = false;
	}

	// Delete afterwards to ensure that callFn is called once when action === 1
	var _iteratorNormalCompletion4 = true;
	var _didIteratorError4 = false;
	var _iteratorError4 = undefined;

	try {
		for (var _iterator4 = Object.keys(this._actions)[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
			var _name = _step4.value;

			if (actions[_name] === 1) {
				if (this._actions[_name].resolveFn) {
					this._actions[_name].resolveFn();
				}

				this._actionEnded = true;

				delete this._actions[_name];
			}
		}
	} catch (err) {
		_didIteratorError4 = true;
		_iteratorError4 = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion4 && _iterator4.return) {
				_iterator4.return();
			}
		} finally {
			if (_didIteratorError4) {
				throw _iteratorError4;
			}
		}
	}
};

/**
 * The second function called on requestAnimationFrame. By now, all the
 * properties to be changed should have been called, so this function just
 * iterates through and sets them. Don't call the `callFn` function here or do
 * anything that could cause the style to be recalculated.
 *
 * Here's a list of things not to do: https://gist.github.com/paulirish/5d52fb081b3570c81e3a
 *
 * @private
 */
Transformer$1.prototype._frame = function transformFrame(vars) {
	if (!this.active) {
		return;
	}

	var _iteratorNormalCompletion7 = true;
	var _didIteratorError7 = false;
	var _iteratorError7 = undefined;

	try {
		for (var _iterator7 = this.transforms[Symbol.iterator](), _step7; !(_iteratorNormalCompletion7 = (_step7 = _iterator7.next()).done); _iteratorNormalCompletion7 = true) {
			var _transform = _step7.value;

			if (_transform._stagedData.isHidden) {
				each(_transform.el, function (el) {
					if (typeof getData(el, 'originalDisplay') === 'undefined') {
						setData(el, 'originalDisplay', el.style.display || '');
					}

					el.style.display = 'none';
				});

				continue;
			} else {
				each(_transform.el, function (el) {
					el.style.display = getData(el, 'originalDisplay');
				});
			}

			if (_transform.transforms) {
				(function () {
					var transforms = _transform._stagedData.transforms;

					if (transforms !== UNCHANGED) {
						transforms = transforms.map(function (_ref3) {
							var _ref4 = slicedToArray(_ref3, 2),
							    key = _ref4[0],
							    value = _ref4[1];

							return key + '(' + value + ')';
						}).join(' ');

						each(_transform.el, function (el) {
							if (useTransformAttr(el)) {
								el.setAttribute('transform', getData(el, 'originalTransform') + transforms);
							} else {
								el.style.transform = getData(el, 'originalTransform') + transforms;
							}
						});
					}
				})();
			}

			if (_transform.styles) {
				var _iteratorNormalCompletion8 = true;
				var _didIteratorError8 = false;
				var _iteratorError8 = undefined;

				try {
					var _loop2 = function _loop2() {
						var _step8$value = slicedToArray(_step8.value, 1),
						    style = _step8$value[0];

						var computed = _transform._stagedData.styles[style];

						if (computed === UNCHANGED) {
							return 'continue';
						}

						each(_transform.el, function (el) {
							if (Array.isArray(style)) {
								style.forEach(function (style) {
									el.style[style] = computed;
								});
							} else {
								el.style[style] = computed;
							}
						});
					};

					for (var _iterator8 = _transform.styles[Symbol.iterator](), _step8; !(_iteratorNormalCompletion8 = (_step8 = _iterator8.next()).done); _iteratorNormalCompletion8 = true) {
						var _ret3 = _loop2();

						if (_ret3 === 'continue') continue;
					}
				} catch (err) {
					_didIteratorError8 = true;
					_iteratorError8 = err;
				} finally {
					try {
						if (!_iteratorNormalCompletion8 && _iterator8.return) {
							_iterator8.return();
						}
					} finally {
						if (_didIteratorError8) {
							throw _iteratorError8;
						}
					}
				}
			}

			if (_transform.attrs) {
				var _iteratorNormalCompletion9 = true;
				var _didIteratorError9 = false;
				var _iteratorError9 = undefined;

				try {
					var _loop3 = function _loop3() {
						var _step9$value = slicedToArray(_step9.value, 1),
						    attr = _step9$value[0];

						var computed = _transform._stagedData.attrs[attr];

						if (computed === UNCHANGED) {
							return 'continue';
						}

						each(_transform.el, function (el) {
							if (Array.isArray(attr)) {
								attr.forEach(function (attr) {
									el.setAttribute(attr, computed);
								});
							} else {
								el.setAttribute(attr, computed);
							}
						});
					};

					for (var _iterator9 = _transform.attrs[Symbol.iterator](), _step9; !(_iteratorNormalCompletion9 = (_step9 = _iterator9.next()).done); _iteratorNormalCompletion9 = true) {
						var _ret4 = _loop3();

						if (_ret4 === 'continue') continue;
					}
				} catch (err) {
					_didIteratorError9 = true;
					_iteratorError9 = err;
				} finally {
					try {
						if (!_iteratorNormalCompletion9 && _iterator9.return) {
							_iterator9.return();
						}
					} finally {
						if (_didIteratorError9) {
							throw _iteratorError9;
						}
					}
				}
			}
		}
	} catch (err) {
		_didIteratorError7 = true;
		_iteratorError7 = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion7 && _iterator7.return) {
				_iterator7.return();
			}
		} finally {
			if (_didIteratorError7) {
				throw _iteratorError7;
			}
		}
	}

	var currentTime = window.performance ? performance.now() : Date.now();
	var deltaTime = currentTime - this._lastTime;

	var increaseBy = {
		count: 1,
		time: deltaTime / (1000 / this.iIncrease.optimalFps)
	};

	if (!this._lastTime) {
		this.i++;
	} else if (deltaTime > 1000 / this.iIncrease.optimalFps) {
		this.i += increaseBy[this.iIncrease.belowOptimal];
	} else {
		this.i += increaseBy[this.iIncrease.aboveOptimal];
	}

	this._last = vars;
	this._lastTime = currentTime;
};

/**
 * Given a domain and a range, transforms a number into another number.
 *
 * See the README.
 *
 * @param {[number, number]} domain 2-dimensional domain.
 * @param {[number, number]} range 2-dimensional range.
 * @param {number} [val] The number to transform.
 * @param {boolean} [fixed=true] Restrict the number to the range.
 * @returns {number|function} Transformed number.
 */
function transform(domain, range, val, fixed) {
	if (typeof val === 'undefined') {
		return function (val2) {
			return transform(domain, range, val2, fixed);
		};
	}

	var normalised = (val - domain[0]) / (domain[1] - domain[0]);

	if (fixed !== false) {
		normalised = Math.min(Math.max(normalised, 0), 1);
	}

	return (range[1] - range[0]) * normalised + range[0];
}

// Easings that can be used by transformObj. Each function should take a
// number between 0 and 1 (a percentage) and return a different number between
// 0 and 1. Feel free to send PRs.
var easings = {
	linear: function linear(x) {
		return x;
	},
	easeInQuad: function easeInQuad(x) {
		return Math.pow(x, 2);
	},
	easeOutQuad: function easeOutQuad(x) {
		return 1 - Math.pow(1 - x, 2);
	},
	easeInOutQuad: function easeInOutQuad(x) {
		return function (x) {
			return x < 0.5 ? Math.pow(x * 2, 2) / 2 : 1 - Math.pow(2 * (1 - x), 2) / 2;
		};
	},
	easeInCubic: function easeInCubic(x) {
		return Math.pow(x, 3);
	},
	easeOutCubic: function easeOutCubic(x) {
		return 1 - Math.pow(1 - x, 3);
	},
	easeInOutCubic: function easeInOutCubic(x) {
		return function (x) {
			return x < 0.5 ? Math.pow(x * 2, 3) / 2 : 1 - Math.pow(2 * (1 - x), 3) / 2;
		};
	},
	easeInQuart: function easeInQuart(x) {
		return Math.pow(x, 4);
	},
	easeOutQuart: function easeOutQuart(x) {
		return 1 - Math.pow(1 - x, 4);
	},
	easeInOutQuart: function easeInOutQuart(x) {
		return function (x) {
			return x < 0.5 ? Math.pow(x * 2, 4) / 2 : 1 - Math.pow(2 * (1 - x), 4) / 2;
		};
	},
	easeInQuint: function easeInQuint(x) {
		return Math.pow(x, 5);
	},
	easeOutQuint: function easeOutQuint(x) {
		return 1 - Math.pow(1 - x, 5);
	},
	easeInOutQuint: function easeInOutQuint(x) {
		return function (x) {
			return x < 0.5 ? Math.pow(x * 2, 5) / 2 : 1 - Math.pow(2 * (1 - x), 5) / 2;
		};
	}
};

/**
 * Given an object to transform by, transforms a number into another number.
 *
 * See the README.
 *
 * @param {object} obj The object containing the values to transform around.
 * @param {number} [loopBy] The number to restart the transform at.
 * @param {string|function} [easing] Easing function.
 * @returns {Function} Function that takes a number to transform.
 */
function transformObj(obj, loopBy, easing) {
	var keys = Object.keys(obj).sort(function (a, b) {
		return a - b;
	});
	var keysBackwards = keys.slice().reverse();

	if (typeof easing === 'string') {
		easing = easings[easing];
	}

	return function (val) {
		if (loopBy && typeof easing === 'function') {
			val = easing(val % loopBy / loopBy) * loopBy;
		} else if (loopBy) {
			val %= loopBy;
		}

		if (val <= keys[0]) {
			return obj[keys[0]];
		}

		if (val >= keysBackwards[0] && !loopBy) {
			return obj[keysBackwards[0]];
		}

		var fromIndex = keys.length - 1 - keysBackwards.findIndex(function (key) {
			return val >= key;
		});

		var from = keys[fromIndex];
		var to = keys[fromIndex + 1] || loopBy;

		var toVal = to === loopBy ? obj[keys[0]] : obj[to];
		return transform([Number(from), Number(to)], [obj[from], toVal], val);
	};
}

// Cache element lookups here so that we don't have to look them up at 60 fps
var elements = {};

/**
 * This function powers transform-when. It is called on requestAnimationFrame,
 * and calls the transform functions.
 */
function runFrames() {
	var scrollPositions = {
		window: {
			x: typeof window.scrollX === 'number' ? window.scrollX : window.pageXOffset,
			y: typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset
		}
	};

	// Two loops: the first runs "setup", which calculates all the values to set
	var _iteratorNormalCompletion = true;
	var _didIteratorError = false;
	var _iteratorError = undefined;

	try {
		var _loop = function _loop() {
			var transform = _step.value;

			if (!scrollPositions[transform.scrollElement]) {
				if (!elements[transform.scrollElement]) {
					elements[transform.scrollElement] = document.querySelector(transform.scrollElement);
				}

				scrollPositions[transform.scrollElement] = {
					x: elements[transform.scrollElement].scrollLeft,
					y: elements[transform.scrollElement].scrollTop
				};
			}

			var position = scrollPositions[transform.scrollElement];

			var vars = { x: position.x, y: position.y };
			Object.keys(transform._customVariables).forEach(function (varName) {
				vars[varName] = transform._customVariables[varName].call(transform);
			});

			// This is ugly and I feel bad
			transform._tmpVarCache = vars;

			try {
				transform._setup(vars);
			} catch (e) {
				console.error('Problem during setup', e);
			}
		};

		for (var _iterator = transformers[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
			_loop();
		}

		// The second loop calls "frame", which sets all the previously calculated values
		// It's done in two loops to avoid layout thrashing
	} catch (err) {
		_didIteratorError = true;
		_iteratorError = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion && _iterator.return) {
				_iterator.return();
			}
		} finally {
			if (_didIteratorError) {
				throw _iteratorError;
			}
		}
	}

	var _iteratorNormalCompletion2 = true;
	var _didIteratorError2 = false;
	var _iteratorError2 = undefined;

	try {
		for (var _iterator2 = transformers[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
			var _transform = _step2.value;

			try {
				_transform._frame(_transform._tmpVarCache);
			} catch (e) {
				console.error('Problem during frame', e);
			}
		}
	} catch (err) {
		_didIteratorError2 = true;
		_iteratorError2 = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion2 && _iterator2.return) {
				_iterator2.return();
			}
		} finally {
			if (_didIteratorError2) {
				throw _iteratorError2;
			}
		}
	}

	requestAnimationFrame(runFrames);
}
requestAnimationFrame(runFrames);

/**
 * Hello! Best place to start is probably run-frames.js and Transformer.js.
 */

Transformer$1.transform = transform;
Transformer$1.easings = easings;
Transformer$1.transformObj = transformObj;

return Transformer$1;

})));
//# sourceMappingURL=transform-when.js.map
